import { pluralize } from '@gonfalon/format/pluralize';
import { isEqual, kebabCase } from 'lodash';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import type { z } from 'zod';

import type { RegisteredSchemaUnion, RegisteredVariation, VariationsFormSchema } from './registry';

const KEYS_NEVER_SENT: ReadonlySet<string> = new Set(['createdAt', 'version', 'state']);
const DIFF_HIDDEN_KEYS: ReadonlySet<string> = new Set([...KEYS_NEVER_SENT, 'key', 'modelConfigKey']);

export function getVariationShape(schema: VariationsFormSchema): Record<string, z.ZodTypeAny> {
  return schema.shape.variations.innerType().element.shape as Record<string, z.ZodTypeAny>;
}

export function getPayloadKeys(schema: VariationsFormSchema): string[] {
  return Object.keys(getVariationShape(schema)).filter((k) => !KEYS_NEVER_SENT.has(k));
}

export function getDiffKeys(schema: VariationsFormSchema): string[] {
  return Object.keys(getVariationShape(schema)).filter((k) => !DIFF_HIDDEN_KEYS.has(k));
}

export function getEffectiveKey(variation: { key: string; name: string }): string {
  return variation.key || kebabCase(variation.name);
}

export function getSaveSuccessToastTitle(created: number, updated: number): string {
  if (created > 0 && updated === 0) {
    return `${pluralize(created, 'Variation', 'Variations')} created successfully`;
  }
  if (updated > 0 && created === 0) {
    return `${pluralize(updated, 'Variation', 'Variations')} updated successfully`;
  }

  return 'Variations saved successfully';
}

/** Normalizes tools (undefined → []) so they compare as equal when variation is expanded
 * without actually changing the form value which could cause tools to get removed on save.
 */
export function normalizeVariationTools(v: RegisteredVariation | undefined): Record<string, unknown> | undefined {
  if (!v) {
    return;
  }
  const out = { ...v } as Record<string, unknown>;
  if (!Array.isArray(out.tools)) {
    out.tools = [];
  }
  return out;
}

/**
 * Strips server-managed fields and normalizes tools for dirty-state comparison.
 * `version`, `createdAt`, and `state` are managed by the server and change on
 * every save. Including them in the comparison causes false "dirty" detection
 * when the form's current values and defaultValues briefly hold different
 * server versions during the async save → refresh → reset cycle.
 */
function normalizeForComparison(v: RegisteredVariation): Record<string, unknown> {
  const out = { ...v } as Record<string, unknown>;
  for (const key of KEYS_NEVER_SENT) {
    delete out[key];
  }
  if (!Array.isArray(out.tools)) {
    out.tools = [];
  }
  return out;
}

export function variationHasChanges(original: RegisteredVariation | undefined, compare: RegisteredVariation): boolean {
  if (!original) {
    return true;
  }
  return !isEqual(normalizeForComparison(original), normalizeForComparison(compare));
}

export function filterToChangedVariations<T extends RegisteredSchemaUnion>(
  data: T,
  originalVariations: T['variations'],
): T {
  const originalLookup = new Map((originalVariations as RegisteredVariation[]).map((v) => [v.key, v] as const));

  const changedVariations = (data.variations as RegisteredVariation[]).filter((v) => {
    const original = originalLookup.get(v.key);
    return variationHasChanges(original, v);
  });

  return { ...data, variations: changedVariations } as T;
}

/**
 * Runs `fn` after two nested `requestAnimationFrame` ticks (one frame after the next), not on the immediate next frame.
 * Useful so layout (refs, scroll metrics) is usually settled before the callback runs.
 * Widely used workaround for waiting until after paint when layout and refs are usually ready.
 */
export function runAfterNextLayoutFrame(fn: () => void): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(fn);
  });
}

export function scrollElementIntoView(fromElement: HTMLElement | null | (() => HTMLElement | null)): void {
  if (typeof window === 'undefined') {
    return;
  }

  runAfterNextLayoutFrame(() => {
    const el = typeof fromElement === 'function' ? fromElement() : fromElement;
    if (el) {
      // Honor OS/browser reduced-motion: avoid animated scroll when the user prefers less motion.
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end', inline: 'nearest' });
    }
  });
}
